import { useEffect, useRef, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Modal,
  Text,
} from "@shopify/polaris";
import {
  comparisonRequest,
  type ComparisonAction,
} from "../../../../packages/shared/stock-comparison/client";
import type { QueueAccount } from "../../../../packages/shared/shopify-queue/client";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
type Preview = {
  ready: boolean;
  reasons: string[];
  preview_hash: string;
  store: string | null;
  connection_id: string | null;
  product_count: number;
  warehouse_count: number;
  location_count: number;
  target_count: number;
};
export function ComparisonStart({
  account,
  disabled,
  connectionId,
  onAction,
}: {
  account: QueueAccount;
  disabled: boolean;
  connectionId: string | null;
  onAction: (action: ComparisonAction) => Promise<void>;
}) {
  const [review, setReview] = useState<Preview | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const prepare = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await comparisonRequest<Preview>(base, account, "/preview?connection_id=" + encodeURIComponent(connectionId || ""));
      if (alive.current) setReview(data);
    } catch (e) {
      if (alive.current)
        setError(
          e instanceof Error
            ? e.message
            : "Could not load the comparison review",
        );
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Compare warehouse stock with Shopify
        </Text>
        <Text as="p">
          Check every mapped product and location, including zero stock. A saved
          report shows differences and missing stock records. It does not change
          stock in Whenly or Shopify.
        </Text>
        <Text as="p">
          Checks continue after you close this page. Each result shows when it
          was checked. A large catalogue may need several passes to finish.
        </Text>
        {error && <Banner tone="critical">{error}</Banner>}
        <Button
          loading={busy}
          disabled={disabled || busy}
          onClick={() => void prepare()}
        >
          Review stock comparison
        </Button>
        {disabled && (
          <Text as="p">
            Choose a connected store and wait for its status to load. Finish, retry or cancel
            that store’s active comparison before starting another.
          </Text>
        )}
        <Modal
          title="Review stock comparison"
          open={!!review}
          onClose={() => setReview(null)}
          primaryAction={{
            content: "Start stock comparison",
            disabled: disabled || busy || !review?.ready,
            onAction: () => {
              if (review?.ready) {
                const hash = review.preview_hash, connection_id = review.connection_id!;
                setReview(null);
                void onAction({ action: "start", preview_hash: hash, connection_id });
              }
            },
          }}
          secondaryActions={[
            { content: "Cancel", onAction: () => setReview(null) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              {review && (
                <>
                  <Text as="p">
                    Compare {review.product_count} products at{" "}
                    {review.location_count} Shopify locations for{" "}
                    {review.store || "the connected store"}. This saves{" "}
                    {review.target_count} product and location checks across{" "}
                    {review.warehouse_count} warehouses.
                  </Text>
                  {review.reasons.map((reason) => (
                    <Banner key={reason} tone="warning">
                      {reason}
                    </Banner>
                  ))}
                  <Text as="p">
                    Stock is read only. Missing Shopify stock levels appear as
                    needing review. Completed results remain available if later
                    checks fail or you cancel.
                  </Text>
                </>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Card>
  );
}
